import { useState, useContext, useEffect } from 'react';
import { BookingContext } from '../../shared/contexts/Booking.context';
import { useRouter } from 'next/router';
import startCheckinRequest from '../../services/startCheckin.service';
import { showError } from '../../shared/utils/utils';
import errors from '../../shared/config/errors';
import { LanguageContext } from '../../shared/contexts/Language.context';
import * as GtmUtils from '../../shared/utils/GtmUtils';
import { ThemeContext } from '../../shared/contexts/Theme.context';
import restartCheckinStatRequest from '../../services/restartCheckinStat.service';
import { logger } from '../../services/logger/logger.service';
import { resetMetricsCookie } from '../../shared/utils/cookies';

let loaded = true;

function useStartCheckin() {
  const router = useRouter();
  const { state: bookingContextState, actions: bookingContextActions } = useContext(BookingContext);
  const { state: languageContextState } = useContext(LanguageContext);
  const { state: themeState, actions: themeActions } = useContext(ThemeContext);

  const [state, setState] = useState({
    loading: true,
    checkinRequirements: null,
    checkinDisabled: { disabled: false },
  });

  const redirectToGuestForm = router.query.deeplink === 'guestForm' || false;

  const actions = {
    startCheckin: () => {
      const { hotelId } = bookingContextState;
      if (hotelId) {
        const booking = bookingContextState?.checkinRequirements?.booking;
        if (
          bookingContextState.checkinRequirements &&
          (router.query.bookingIdentifier == booking.code ||
            router.query.bookingIdentifier == booking.uuid) &&
          !hasLocalState(bookingContextState.checkinRequirements?.checkinGuests) &&
          !redirectToGuestForm
        ) {
          updateState({
            loading: redirectToGuestForm,
            checkinRequirements: bookingContextState.checkinRequirements,
            bookingToCheckin: bookingContextState.checkinRequirements.booking,
            checkinDisabled: checkDisabled(bookingContextState.checkinRequirements),
            someAdultFinished: checkSomeAdultFinished(bookingContextState.checkinRequirements),
            showHolderData: containsBookingData(booking),
          });
        } else {
          if (redirectToGuestForm) {
            resetMetricsCookie();
            bookingContextActions.clearState();
            logger.log({
              type: 'push-notification-step',
              message: 'app-open-checkin',
              pushId: router.query.pushId,
              date: new Date().toISOString(),
              data: {
                language: languageContextState.selectedLanguage,
                enableCache: router.query.enableCache,
                source: router.query.source,
              },
            });
          }
          startCheckinRequest(
            hotelId,
            router.query.bookingIdentifier,
            languageContextState.selectedLanguage,
            router.query.enableCache,
            router.query.source
          )
            .then(checkinRequirements => {
              if (checkinRequirements.booking) {
                updateState({
                  loading: redirectToGuestForm,
                  checkinRequirements,
                  bookingToCheckin: checkinRequirements.booking,
                  checkinDisabled: checkDisabled(checkinRequirements),
                  someAdultFinished: checkSomeAdultFinished(checkinRequirements),
                  showHolderData: containsBookingData(checkinRequirements.booking),
                });
                bookingContextActions.setCheckinRequirements(checkinRequirements);
              } else if (checkinRequirements.bookings) {
                bookingContextActions.setBookingSearchResults(checkinRequirements.bookings);
                router.push(
                  '/hotel/[hotelId]/bookingSearch',
                  `/hotel/${bookingContextState.hotelId}/bookingSearch`
                );
              }

              const { hotelId, bookingIdentifier, ...customParams } = router.query;
              if (customParams && Object.keys(customParams).length) {
                bookingContextActions.setBookingCustomParameters(customParams, 'allGuests');

                handleRedirectToGuestForm(customParams);
              }
            })
            .catch(error => {
              if (error?.response?.status == 405) {
                actions.goToBookingSearchForm();
              } else {
                updateState({
                  loading: false,
                  pmsMessage: error?.response?.data?.error?.pmsMessage,
                });
              }
            });
        }
      }
    },
    goToGuestForm: checkinGuestId => {
      restartCheckinStatRequest(checkinGuestId).then(_ => {});

      router.push(
        '/hotel/[hotelId]/guestForm/[checkinGuestId]',
        `/hotel/${bookingContextState.hotelId}/guestForm/${checkinGuestId}?step=1`
      );
    },

    goToBookingSearchForm: () => {
      router.push(
        '/hotel/[hotelId]/bookingSearchForm',
        `/hotel/${bookingContextState.hotelId}/bookingSearchForm`
      );
    },
    handleGuestCheckin: guestId => {
      actions.goToGuestForm(guestId);
    },
  };

  useEffect(() => {
    if (
      bookingContextState.hotelId &&
      themeState.startCheckinTimeoutSeconds &&
      state.checkinRequirements
    ) {
      const seconds = themeState.startCheckinTimeoutSeconds;
      const milliseconds = seconds * 1000;
      const timeout = setTimeout(() => {
        if (themeActions.getIsPaperlessMultiproperty()) {
          themeActions.goToMultipropertyLanding();
        } else {
          router.push(
            '/hotel/[hotelId]/languages',
            `/hotel/${bookingContextState.hotelId}/languages`
          );
        }
      }, milliseconds);
      return () => clearTimeout(timeout);
    }
  }, [
    bookingContextState.hotelId,
    themeState.startCheckinTimeoutSeconds,
    state.checkinRequirements,
  ]);

  useEffect(actions.startCheckin, [
    router.query.bookingIdentifier,
    bookingContextState.hotelId,
    JSON.stringify(bookingContextState.checkinRequirements),
  ]);

  useEffect(() => {
    GtmUtils.customPageView({
      hotelName: bookingContextState.hotelId,
      stepName: 'startCheckin',
      bookingIdentifier: bookingContextState?.bookingSearchFormValues?.bookingCode,
      reservationNumber: bookingContextState?.bookingToCheckin?.code,
      chainName: themeState.chainName,
      partner: router.query?.partner,
    });

    loaded = true;
    window.onbeforeunload = function () {
      if (loaded) {
        sessionStorage.clear();
      }
    };
    return () => {
      loaded = false;
    };
  }, []);

  function handleRedirectToGuestForm(customParams) {
    if (redirectToGuestForm && customParams.uuid) {
      bookingContextActions.setIsPushGuestDeeplink({
        isPushGuestDeeplink: true,
        pushId: router.query.pushId,
      });
      actions.goToGuestForm(customParams.uuid);

      setTimeout(() => {
        updateState({
          loading: false,
        });
      }, 2000);
    }
  }

  function updateState(newState) {
    setState(previousState => ({ ...previousState, ...newState }));
  }

  return { state, actions };
}

function hasLocalState(checkinGuests = []) {
  return checkinGuests.some(checkinGuest => checkinGuest.isLocalState);
}

function checkDisabled(checkinRequirements) {
  const checkinDisabled = { disabled: false };
  if (!checkinRequirements?.checkinAvailability?.available) {
    checkinDisabled.disabled = true;
    const { upperLimitHours, downLimitHours } = checkinRequirements?.checkinAvailability;
    checkinDisabled.from =
      downLimitHours >= 24
        ? `${Math.floor(downLimitHours / 24)} days`
        : `${downLimitHours || 0} hours`;
    checkinDisabled.to =
      upperLimitHours >= 24
        ? `${Math.floor(upperLimitHours / 24)} days`
        : `${upperLimitHours || 0} hours`;
  }
  return checkinDisabled;
}

function checkSomeAdultFinished(checkinRequirements) {
  return (
    checkinRequirements?.checkinGuests?.filter(
      checkinGuest => checkinGuest.type == 'adult' && checkinGuest.finished
    ).length > 0
  );
}

function containsBookingData(booking) {
  const bookingAttributes = [
    'entrance',
    'departure',
    'code',
    'holder',
    'name',
  ];
  let counter = 0;

  if (booking) {
    for (const [key, value] of Object.entries(booking)) {
      if (bookingAttributes.includes(key) && value) {
        counter++;
        break;
      }
    }
  }
  return counter > 0;
}

export default useStartCheckin;
